import type {
  AssistantConsultantDeadlinesDraft,
  AssistantDeadlineAiField,
  CreateConsultantDeadlinesAction,
} from '@prospero/shared/src/assistant/assistant-contract';
import type { ClientOption } from '../components/consultant-client-multi-select';
import type { ConsultantDeadlineFormValues } from './consultant-deadline-form.mapper';

export interface AssistantDraftDialogValues {
  /** The card the dialog closes when saved: the rows carry it, a later "Crea" adds nothing. */
  assistantActionId: string;
  clients: ClientOption[];
  values: ConsultantDeadlineFormValues;
  /** Token staged by the assistant: the dialog adopts it, no new upload or reading. */
  document: { uploadToken: string; filename: string } | null;
  /** Fields the AI read from the document: empty when the assistant did not read it. */
  aiFields: AssistantDeadlineAiField[];
}

/**
 * "Chi carica il documento?" as the dialog asks it: with a document the consultant uploads it
 * (one client); several clients without a document upload it themselves; one client keeps the
 * choice of the draft (the consultant then attaches the file, as in the plain dialog).
 */
function resolveClientCanUpload(draft: AssistantConsultantDeadlinesDraft): boolean {
  if (draft.document) {
    return false;
  }
  return draft.clients.length > 1 || draft.clientCanUpload;
}

/** The card's draft as the "Nuova scadenza" form, for those who prefer to correct it by hand. */
export function toAssistantDraftDialogValues(
  action: CreateConsultantDeadlinesAction,
): AssistantDraftDialogValues {
  const { draft } = action;
  // A draft with a document always has one client; the dialog enforces it for "Lo carico io".
  const draftClients = draft.document ? draft.clients.slice(0, 1) : draft.clients;
  return {
    assistantActionId: action.id,
    clients: draftClients.map((client) => ({ tenantId: client.tenantId, tenantName: client.name })),
    values: {
      tenantIds: draftClients.map((client) => client.tenantId),
      documentTypeId: draft.documentTypeId ?? '',
      titleRequired: draft.title !== null,
      title: draft.title ?? '',
      domain: draft.title !== null ? (draft.domain ?? undefined) : undefined,
      recurrence: draft.validityMonths !== null ? 'RECURRING' : 'ONE_OFF',
      dueAt: draft.dueAt,
      validityMonths: draft.validityMonths ?? undefined,
      warningDays: draft.warningDays,
      clientCanUpload: resolveClientCanUpload(draft),
      requestClientSignature: false,
      assigneeUserId: null,
    },
    document: draft.document
      ? { uploadToken: draft.document.uploadToken, filename: draft.document.filename }
      : null,
    aiFields: draft.document?.aiFields ?? [],
  };
}
